import { BuildingIcon, EyeIcon, EyeOffIcon } from "lucide-react";
import React from "react";
import type {
    CreateOrganizationRequest,
    Organization,
    PatchOrganizationRequest,
} from "verdi-common/apiTypes/organization";
import { ORGANIZATION_NAME_MAX_LENGTH } from "verdi-common/schemas/organization/organization";
import { formatTime } from "verdi-common/utils/index";
import { SessionHandler } from "verdi-web-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Fieldset, FieldsetLegend } from "@/components/ui/fieldset";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputGroup } from "@/components/ui/input-group";
import { Switch } from "@/components/ui/switch";
import { parseOrganizationCoordinateField } from "./parse-organization-coordinate-field";

function RevealablePasswordInput({
    name,
    placeholder,
    defaultValue,
    readOnly,
}: {
    name: string;
    placeholder?: string;
    defaultValue?: string;
    readOnly?: boolean;
}) {
    const [masked, setMasked] = React.useState(true);
    return (
        <InputGroup>
            <Input
                type={masked ? "password" : "text"}
                name={name}
                placeholder={placeholder}
                defaultValue={defaultValue}
                readOnly={readOnly}
            />
            <Button variant={"ghost"} onClick={() => setMasked(!masked)}>
                {masked ? <EyeIcon className={"size-6"} /> : <EyeOffIcon className={"size-6"} />}
            </Button>
        </InputGroup>
    );
}

export type OrganizationDetailsPanelProps = {
    organization?: Organization;
    onClose: () => void;
    onSave?: (updatedOrganization: Organization) => void;
};

export function OrganizationDetailsPanel({ organization, onClose, onSave }: OrganizationDetailsPanelProps) {
    const [formAlert, setFormAlert] = React.useState<{
        variant: "info" | "error";
        title: string;
        description: React.ReactNode | string;
    } | null>(null);

    const newOrganization = !organization;
    if (!organization) {
        organization = {
            // TODO(jeff): A default for `id` can be dropped after separating create (`id` optional) from update (`id` required).
            id: "",
            name: "",
            sensoterraEmail: "",
            sensoterraPassword: "",
            defaultCoordinates: {},
            createdAt: Date.now().valueOf(),
            deleted: false,
            adminUseOnly: false,
        };
    }

    function getChangedFormValues(
        existingOrganization: Organization,
        formValues: Record<string, FormDataEntryValue | FormDataEntryValue[] | null>,
    ): Record<string, string | string[] | number | boolean | null> {
        const changedFormValues: Record<string, string | string[] | number | boolean | null> = {};

        // Only update if changed
        const stringFields: (keyof Organization)[] = ["name"];
        stringFields.forEach((field) => {
            const value = formValues[field];
            if (typeof value === "string") {
                const trimmedValue = value.trim();
                if (trimmedValue !== existingOrganization?.[field]) {
                    changedFormValues[field] = trimmedValue;
                }
            }
        });

        const numberFields = ["latitude", "longitude"] as const;
        numberFields.forEach((field) => {
            const value = formValues[field];
            if (typeof value === "string") {
                const trimmedValue = parseOrganizationCoordinateField(value);
                if (trimmedValue !== existingOrganization.defaultCoordinates?.[field]) {
                    changedFormValues[field] = trimmedValue;
                }
            }
        });

        const booleanFields = ["deleted", "adminUseOnly"] as const;
        booleanFields.forEach((field) => {
            const value = formValues[field];
            if (typeof value === "string") {
                const trimmedValue = value.trim() === "on";
                if (trimmedValue !== existingOrganization[field]) {
                    changedFormValues[field] = trimmedValue;
                }
            }
        });

        return changedFormValues;
    }

    const organizationSaveHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);
        const formValues = {
            name: formData.get("name"),
            latitude: formData.get("latitude"),
            longitude: formData.get("longitude"),
            deleted: formData.get("deleted"),
            adminUseOnly: formData.get("adminUseOnly"),
        };

        const changedFormValues = getChangedFormValues(organization, formValues);

        const patchOrganizationRequest = newOrganization
            ? (changedFormValues as CreateOrganizationRequest)
            : ({ id: organization.id, ...changedFormValues } as PatchOrganizationRequest);
        const needsPatch = Object.keys(changedFormValues).length > 0;
        if (needsPatch) {
            // setFormAlert({ variant: "info", title: "Saving", description: <pre className={"font-mono"}>{`${JSON.stringify(organization, null, 2)}\n\n${JSON.stringify(patchOrganizationRequest, null, 2)}`}</pre> });
            try {
                const updateResponse = await SessionHandler.doFetch("organizations/update", {
                    headers: { "Content-Type": "application/json" },
                    method: "POST",
                    body: JSON.stringify(patchOrganizationRequest),
                });
                if (!updateResponse.ok) {
                    throw new Error("Failed to update organization", {
                        cause: await updateResponse.text(),
                    });
                }
                if (onSave) {
                    onSave((await updateResponse.json()).organization);
                }
                onClose();
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
                setFormAlert({
                    variant: "error",
                    title: "Save failed",
                    description: errorMessage,
                });
            }
        } else {
            setFormAlert(null);
            onClose();
        }
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle
                    className={
                        "flex flex-row gap-4 pb-2 mb-0 items-end border-b border-secondary font-bold text-lg md:text-2xl"
                    }
                >
                    <BuildingIcon className={"size-8 md:size-10 stroke-3 stroke-primary"} />
                    {newOrganization ? (
                        "Add Organization"
                    ) : (
                        <Breadcrumb>
                            <BreadcrumbList className={"font-bold text-lg md:text-2xl"}>
                                <BreadcrumbItem>Edit Organization</BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>{organization.name}</BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    )}
                </DialogTitle>
            </DialogHeader>

            <Form onSubmit={organizationSaveHandler} className={"flex min-h-0 w-full flex-1 flex-col gap-4"}>
                <DialogPanel className={"flex flex-col gap-4"}>
                    <DialogDescription>
                        Make changes to the organization here. Click save when you&apos;re done.
                    </DialogDescription>
                    {formAlert && (
                        <Alert variant={formAlert.variant}>
                            <AlertTitle>{formAlert.title}</AlertTitle>
                            <AlertDescription>{formAlert.description}</AlertDescription>
                        </Alert>
                    )}
                    <Fieldset className={"flex w-full flex-col gap-6"}>
                        <FieldsetLegend className={"text-xl border-b border-secondary"}>
                            Basic Information
                        </FieldsetLegend>
                        {organization.createdAt && (
                            <Field className={"grow"}>
                                <FieldLabel>Created {formatTime(organization.createdAt)}</FieldLabel>
                            </Field>
                        )}
                        <Field className={"grow"}>
                            <FieldLabel>Name</FieldLabel>
                            <Input
                                name={"name"}
                                required
                                type={"text"}
                                maxLength={ORGANIZATION_NAME_MAX_LENGTH}
                                defaultValue={organization.name}
                            />
                            {/* `maxLength` stops a longer name being typed, so missing is the only error left. */}
                            <FieldError className={"text-destructive"}>Organization name is required</FieldError>
                        </Field>
                        <div className={"flex flex-row gap-4"}>
                            <Field className={"grow"}>
                                <FieldLabel>Latitude</FieldLabel>
                                <Input
                                    name={"latitude"}
                                    type={"text"}
                                    defaultValue={organization.defaultCoordinates?.latitude}
                                />
                            </Field>
                            <Field className={"grow"}>
                                <FieldLabel>Longitude</FieldLabel>
                                <Input
                                    name={"longitude"}
                                    type={"text"}
                                    defaultValue={organization.defaultCoordinates?.longitude}
                                />
                            </Field>
                        </div>
                    </Fieldset>
                    <Fieldset className={"flex w-full flex-col gap-6"}>
                        <FieldsetLegend className={"text-xl border-b border-secondary"}>
                            Sensoterra Integration
                        </FieldsetLegend>
                        <Field className={"grow"}>
                            <FieldLabel>Sensoterra Email</FieldLabel>
                            <Input
                                name={"sensoterraEmail"}
                                type={"text"}
                                defaultValue={organization.sensoterraEmail}
                                readOnly
                            />
                        </Field>
                        <Field className={"grow"}>
                            <FieldLabel>Sensoterra Password</FieldLabel>
                            <RevealablePasswordInput
                                name={"sensoterraPassword"}
                                defaultValue={organization.sensoterraPassword}
                                readOnly
                            />
                        </Field>
                    </Fieldset>
                    <Fieldset className={"flex w-full flex-col gap-6"}>
                        <FieldsetLegend className={"text-xl border-b border-secondary"}>Flags</FieldsetLegend>
                        <Field className={"flex flex-row justify-between"}>
                            <FieldLabel className={"w-40"}>Deleted</FieldLabel>
                            <Switch name={"deleted"} defaultChecked={organization.deleted} />
                        </Field>
                        <Field className={"flex flex-row justify-between"}>
                            <FieldLabel className={"w-40"}>Admin Use Only</FieldLabel>
                            <Switch name={"adminUseOnly"} defaultChecked={organization.adminUseOnly} />
                        </Field>
                    </Fieldset>
                </DialogPanel>
                <DialogFooter>
                    <Button variant={"outline"} onClick={() => onClose()}>
                        Cancel
                    </Button>
                    <Button type={"submit"}>Save</Button>
                </DialogFooter>
            </Form>
        </>
    );
}
